export type RegistrationSearchAppointment = {
  slot_id?: string | null;
  label?: string | null;
  starts_at?: string | null;
  ends_at?: string | null;
};

export type RegistrationSearchRow = {
  full_name?: string | null;
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
  ticket_type_name?: string | null;
  status?: string | null;
  quantity?: number | null;
  form_response?: unknown;
  appointments?: RegistrationSearchAppointment[];
};

export type RegistrationSearchAttendee = {
  fullName: string;
  email: string;
  phone: string;
};

export type RegistrationAttendeePerson = RegistrationSearchAttendee & {
  index: number;
};

const ATTENDEE_NAME_KEYS = ['full_name', 'fullName', 'name', 'attendee_name', 'attendeeName'];
const ATTENDEE_EMAIL_KEYS = ['email', 'email_address', 'emailAddress'];
const ATTENDEE_PHONE_KEYS = ['phone', 'phone_number', 'phoneNumber', 'mobile', 'mobile_number'];
const FIRST_NAME_KEYS = ['first_name', 'firstName'];
const LAST_NAME_KEYS = ['last_name', 'lastName'];

const pickString = (record: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = String(record[key] || '').trim();
    if (value) return value;
  }
  return '';
};

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

const getObjectArray = (record: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = record[key];
    if (Array.isArray(value)) {
      return value.filter(
        (entry): entry is Record<string, unknown> => Boolean(entry) && typeof entry === 'object' && !Array.isArray(entry)
      );
    }
  }
  return [] as Record<string, unknown>[];
};

const composePersonName = (record: Record<string, unknown>) => {
  const fullName = pickString(record, ATTENDEE_NAME_KEYS);
  if (fullName) return fullName;
  return [pickString(record, FIRST_NAME_KEYS), pickString(record, LAST_NAME_KEYS)].filter(Boolean).join(' ');
};

const sameNormalized = (left: string, right: string) =>
  Boolean(left) && Boolean(right) && left.toLowerCase() === right.toLowerCase();

const findMatchingAttendeeIndex = (
  attendees: RegistrationSearchAttendee[],
  candidate: RegistrationSearchAttendee
) =>
  attendees.findIndex((existing) => {
    if (sameNormalized(existing.email, candidate.email)) return true;
    if (!sameNormalized(existing.fullName, candidate.fullName)) return false;
    const emailCompatible = !existing.email || !candidate.email || sameNormalized(existing.email, candidate.email);
    const phoneCompatible = !existing.phone || !candidate.phone || sameNormalized(existing.phone, candidate.phone);
    return emailCompatible && phoneCompatible;
  });

export const collectRegistrationSearchAttendees = (
  registration: RegistrationSearchRow
): RegistrationSearchAttendee[] => {
  const attendees: RegistrationSearchAttendee[] = [];

  const addAttendee = (fullName: string, email: string, phone: string) => {
    const attendee = {
      fullName: String(fullName || '').trim(),
      email: String(email || '').trim(),
      phone: String(phone || '').trim(),
    };
    if (!attendee.fullName && !attendee.email && !attendee.phone) return;
    const existingIndex = findMatchingAttendeeIndex(attendees, attendee);
    if (existingIndex >= 0) {
      const existing = attendees[existingIndex];
      attendees[existingIndex] = {
        fullName: existing.fullName || attendee.fullName,
        email: existing.email || attendee.email,
        phone: existing.phone || attendee.phone,
      };
      return;
    }
    attendees.push(attendee);
  };

  addAttendee(String(registration.full_name || ''), String(registration.email || ''), String(registration.phone || ''));

  const responseRecord = asRecord(registration.form_response);
  const primaryContact = asRecord(responseRecord.primary_contact || responseRecord.primarycontact);
  addAttendee(composePersonName(primaryContact), pickString(primaryContact, ATTENDEE_EMAIL_KEYS), pickString(primaryContact, ATTENDEE_PHONE_KEYS));

  for (const member of getObjectArray(responseRecord, ['group_members', 'groupmembers'])) {
    addAttendee(composePersonName(member), pickString(member, ATTENDEE_EMAIL_KEYS), pickString(member, ATTENDEE_PHONE_KEYS));
  }

  for (const entry of getObjectArray(responseRecord, ['attendee_responses', 'attendeeresponses'])) {
    const nestedPerson = asRecord(entry.responses);
    addAttendee(
      String(entry.attendee_name || '').trim() || composePersonName(entry) || composePersonName(nestedPerson),
      pickString(entry, ATTENDEE_EMAIL_KEYS) || pickString(nestedPerson, ATTENDEE_EMAIL_KEYS),
      pickString(entry, ATTENDEE_PHONE_KEYS) || pickString(nestedPerson, ATTENDEE_PHONE_KEYS)
    );
  }

  return attendees;
};

export const getAdditionalAttendeeNames = (registration: RegistrationSearchRow): string[] => {
  const names: string[] = [];
  const seen = new Set<string>();
  for (const attendee of collectRegistrationSearchAttendees(registration).slice(1)) {
    const name = attendee.fullName.trim();
    const normalized = name.toLowerCase();
    if (!name || seen.has(normalized)) continue;
    seen.add(normalized);
    names.push(name);
  }
  return names;
};

export const buildRegistrationAttendeePeople = (
  registration: RegistrationSearchRow,
  extraSlotCount = 0
): RegistrationAttendeePerson[] => {
  const responseRecord = asRecord(registration.form_response);
  const contacts: RegistrationSearchAttendee[] = [
    {
      fullName: String(registration.full_name || '').trim(),
      email: String(registration.email || '').trim(),
      phone: String(registration.phone || '').trim(),
    },
  ];

  for (const member of getObjectArray(responseRecord, ['group_members', 'groupmembers'])) {
    const fullName = composePersonName(member);
    const email = pickString(member, ATTENDEE_EMAIL_KEYS);
    const phone = pickString(member, ATTENDEE_PHONE_KEYS);
    if (!fullName && !email && !phone) continue;
    contacts.push({ fullName, email, phone });
  }

  const responsesByIndex = new Map<number, { attendeeName: string; email: string; phone: string }>();
  const responseRows = getObjectArray(responseRecord, ['attendee_responses', 'attendeeresponses']);
  for (let fallbackIndex = 0; fallbackIndex < responseRows.length; fallbackIndex += 1) {
    const entry = responseRows[fallbackIndex];
    const nestedPerson = asRecord(entry.responses);
    const attendeeIndexRaw = Number(entry.attendee_index);
    const attendeeIndex =
      Number.isFinite(attendeeIndexRaw) && attendeeIndexRaw >= 0 ? Math.floor(attendeeIndexRaw) : fallbackIndex;
    responsesByIndex.set(attendeeIndex, {
      attendeeName:
        String(entry.attendee_name || '').trim() || composePersonName(entry) || composePersonName(nestedPerson),
      email: pickString(entry, ATTENDEE_EMAIL_KEYS) || pickString(nestedPerson, ATTENDEE_EMAIL_KEYS),
      phone: pickString(entry, ATTENDEE_PHONE_KEYS) || pickString(nestedPerson, ATTENDEE_PHONE_KEYS),
    });
  }

  const totalRows = Math.max(
    1,
    Number(registration.quantity || 1),
    contacts.length,
    responsesByIndex.size ? Math.max(...Array.from(responsesByIndex.keys())) + 1 : 0,
    extraSlotCount
  );

  return Array.from({ length: totalRows }, (_, index) => {
    const contact = contacts[index];
    const responseMeta = responsesByIndex.get(index);
    const fullName = contact?.fullName || responseMeta?.attendeeName || `Attendee ${index + 1}`;
    return {
      index,
      fullName,
      email: contact?.email || responseMeta?.email || '',
      phone: contact?.phone || responseMeta?.phone || '',
    };
  });
};

export const collectAttendeeResponsesByIndex = (formResponse: unknown) => {
  const responseRecord = asRecord(formResponse);
  const responsesByIndex = new Map<number, Record<string, unknown>>();
  const responseRows = getObjectArray(responseRecord, ['attendee_responses', 'attendeeresponses']);
  for (let fallbackIndex = 0; fallbackIndex < responseRows.length; fallbackIndex += 1) {
    const entry = responseRows[fallbackIndex];
    const attendeeIndexRaw = Number(entry.attendee_index);
    const attendeeIndex =
      Number.isFinite(attendeeIndexRaw) && attendeeIndexRaw >= 0 ? Math.floor(attendeeIndexRaw) : fallbackIndex;
    responsesByIndex.set(attendeeIndex, asRecord(entry.responses));
  }
  return responsesByIndex;
};

export type RegistrantExportDirectoryRow = {
  attendeeIndex: number;
  isPrimary: boolean;
  fullName: string;
  email: string;
  phone: string;
  ticket?: {
    attendee_name?: string | null;
    ticket_number?: string | null;
    status?: string | null;
    checked_in_at?: string | null;
  } | null;
  registration: RegistrationSearchRow & {
    id?: string;
    event_id?: string;
    user_id?: string | null;
    ticket_type_id?: string | null;
    payment_status?: string | null;
    source?: string | null;
    created_at?: string | null;
    total_amount_cents?: number | null;
    amount_paid_cents?: number | null;
    balance_due_cents?: number | null;
    payment_amount_cents?: number | null;
    events?: {
      title?: string | null;
      start_date?: string | null;
      start_time?: string | null;
      location?: string | null;
    } | null;
  };
};

export type RegistrantExportAttendeeRow = {
  attendeeIndex: number;
  isPrimary: boolean;
  fullName: string;
  email: string;
  phone: string;
  ticketNumber: string;
  checkedIn: boolean;
  checkinTime: string;
  responses: Record<string, unknown>;
  primaryRegistrantName: string;
  quantity: number;
  registration: RegistrantExportDirectoryRow['registration'];
};

const isExportTicketCheckedIn = (ticket: { status?: string | null } | null | undefined) =>
  String(ticket?.status || '').trim().toLowerCase() === 'checked_in';

export const buildRegistrantExportAttendeeRows = (
  directoryRows: RegistrantExportDirectoryRow[]
): RegistrantExportAttendeeRow[] => {
  const responsesCache = new Map<RegistrationSearchRow, Map<number, Record<string, unknown>>>();
  return directoryRows.map((row) => {
    let responsesByIndex = responsesCache.get(row.registration);
    if (!responsesByIndex) {
      responsesByIndex = collectAttendeeResponsesByIndex(row.registration.form_response);
      responsesCache.set(row.registration, responsesByIndex);
    }
    const ticketName = String(row.ticket?.attendee_name || '').trim();
    return {
      attendeeIndex: row.attendeeIndex,
      isPrimary: row.isPrimary,
      fullName: ticketName || row.fullName,
      email: row.email,
      phone: row.phone,
      ticketNumber: String(row.ticket?.ticket_number || '').trim(),
      checkedIn: isExportTicketCheckedIn(row.ticket),
      checkinTime: String(row.ticket?.checked_in_at || '').trim(),
      responses: responsesByIndex.get(row.attendeeIndex) || {},
      primaryRegistrantName: String(row.registration.full_name || '').trim(),
      quantity: row.isPrimary ? Math.max(1, Number(row.registration.quantity || 1)) : 1,
      registration: row.registration,
    };
  });
};

export const attendeeMatchesSearchQuery = (
  attendee: Pick<RegistrationSearchAttendee, 'fullName' | 'email' | 'phone'>,
  query: string
) => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [attendee.fullName, attendee.email, attendee.phone].some((value) =>
    String(value || '').toLowerCase().includes(q)
  );
};

export const filterDirectoryAttendeesBySearch = <T extends { registration: RegistrationSearchRow } & RegistrationSearchAttendee>(
  rows: T[],
  query: string
): T[] => {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  const grouped = new Map<RegistrationSearchRow, T[]>();
  for (const row of rows) {
    const list = grouped.get(row.registration) ?? [];
    list.push(row);
    grouped.set(row.registration, list);
  }
  const next: T[] = [];
  for (const [registration, group] of Array.from(grouped.entries())) {
    const matchingPeople = group.filter((row) => attendeeMatchesSearchQuery(row, q));
    if (matchingPeople.length > 0) {
      next.push(...matchingPeople);
      continue;
    }
    if (filterRegistrationsBySearch([registration], q).length > 0) {
      next.push(...group);
    }
  }
  return next;
};

const collectSearchFields = (row: RegistrationSearchRow): string[] => {
  const attendees = collectRegistrationSearchAttendees(row);
  return [
    row.full_name,
    row.email,
    row.phone,
    row.notes,
    row.ticket_type_name,
    row.status,
    ...(row.appointments || []).map((appointment) => appointment.label),
    ...attendees.flatMap((attendee) => [attendee.fullName, attendee.email, attendee.phone]),
  ].map((value) => String(value || ''));
};

export const filterRegistrationsBySearch = <T extends RegistrationSearchRow>(list: T[], query: string): T[] => {
  const q = query.trim().toLowerCase();
  if (!q) return list;
  return list.filter((row) => collectSearchFields(row).some((value) => value.toLowerCase().includes(q)));
};
